import React from "react";
import { X, Loader2, IndianRupee } from "lucide-react";

/**
 * PaymentVerificationModal — extracted from ChatsPage.jsx (iter 156.2, Feb 2026).
 *
 * Shows the Claude Sonnet 4.5 Vision verdict on a payment-screenshot. When
 * the verdict is `paid`, the lead is auto-tagged Paid and an audit note is
 * appended to the chat.
 */
export const PaymentVerificationModal = ({ open, onClose, paymentVerdict }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      data-testid="verify-payment-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[--border] flex items-center justify-between" style={{ background: "linear-gradient(135deg, #ECFDF5, #D1FAE5)" }}>
          <div className="flex items-center gap-2">
            <IndianRupee size={18} className="text-emerald-700" />
            <h3 className="text-[15px] font-extrabold text-emerald-900">AI Payment Verification</h3>
          </div>
          <button onClick={onClose} className="text-[--text-muted] hover:text-[--text-primary]" data-testid="close-verify-modal"><X size={18} /></button>
        </div>
        <div className="p-5">
          {!paymentVerdict ? (
            <div className="flex flex-col items-center py-8" data-testid="verify-loading">
              <Loader2 size={32} className="animate-spin text-emerald-600 mb-3" />
              <p className="text-sm font-semibold text-[--text-primary]">Analysing screenshot…</p>
              <p className="text-[11px] text-[--text-muted] mt-1">Claude Sonnet 4.5 Vision — usually 5-15s</p>
            </div>
          ) : (
            <VerdictBody verdict={paymentVerdict} />
          )}
        </div>
      </div>
    </div>
  );
};

const VerdictBody = ({ verdict }) => {
  const status = verdict.status || "suspicious";
  const meta =
    status === "paid"
      ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", emoji: "✅", label: "PAID" }
      : status === "suspicious"
        ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", emoji: "⚠️", label: "SUSPICIOUS" }
        : { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", emoji: "❌", label: "NOT A PAYMENT" };

  const hasExtracted =
    verdict.amount != null ||
    verdict.reference ||
    verdict.upi_id ||
    verdict.payer_name ||
    verdict.paid_to ||
    verdict.timestamp;

  return (
    <>
      <div className={`p-3 rounded-xl border-2 ${meta.bg} ${meta.border} mb-3`} data-testid="verify-verdict-badge">
        <div className="flex items-center justify-between">
          <span className={`text-[15px] font-extrabold ${meta.text}`}>{meta.emoji} {meta.label}</span>
          <span className={`text-[10px] font-bold ${meta.text}`}>{verdict.confidence ?? 0}% confidence</span>
        </div>
        {verdict.reasoning && (
          <p className={`text-[11px] mt-1.5 ${meta.text} leading-relaxed`}>{verdict.reasoning}</p>
        )}
      </div>
      {hasExtracted && (
        <div className="space-y-1.5 p-3 rounded-xl bg-[--bg-cream] border border-[--border]" data-testid="verify-extracted-fields">
          <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-1">Extracted details</p>
          {verdict.amount != null && (
            <div className="flex justify-between text-[12px]"><span className="text-[--text-muted]">Amount</span><span className="font-extrabold text-[--text-primary]">{verdict.currency || "INR"} {verdict.amount}</span></div>
          )}
          {verdict.reference && (
            <div className="flex justify-between text-[12px] gap-2"><span className="text-[--text-muted] flex-shrink-0">Reference</span><span className="font-mono text-[11px] text-[--text-primary] truncate">{verdict.reference}</span></div>
          )}
          {verdict.payer_name && (
            <div className="flex justify-between text-[12px]"><span className="text-[--text-muted]">Payer</span><span className="font-bold text-[--text-primary]">{verdict.payer_name}</span></div>
          )}
          {verdict.paid_to && (
            <div className="flex justify-between text-[12px]"><span className="text-[--text-muted]">Paid to</span><span className="font-bold text-[--text-primary]">{verdict.paid_to}</span></div>
          )}
          {verdict.upi_id && (
            <div className="flex justify-between text-[12px] gap-2"><span className="text-[--text-muted] flex-shrink-0">UPI ID</span><span className="font-mono text-[11px] text-[--text-primary] truncate">{verdict.upi_id}</span></div>
          )}
          {verdict.timestamp && (
            <div className="flex justify-between text-[12px]"><span className="text-[--text-muted]">Time</span><span className="text-[11px] text-[--text-primary]">{verdict.timestamp}</span></div>
          )}
        </div>
      )}
      {status === "paid" && (
        <p className="text-[10px] text-emerald-700 mt-3 text-center" data-testid="paid-label-note">Lead auto-tagged <b>Paid</b>. Audit note added to chat.</p>
      )}
    </>
  );
};
